/**
 * Service Worker 의 마지막 보고 결과를 읽는다 (2026-08-12).
 *
 * ── 왜 필요한가 ─────────────────────────────────────────────
 *
 * `public/sw.js` 의 `report` 는 **설정이 비면 조용히 건너뛰고 실패하면 조용히 삼킨다.**
 * 둘 다 옳은 정책이다 — 계측이 알림을 방해하면 본말이 뒤집힌다(`docs/08` §6). 그런데
 * 그 결과 **「알림은 뜨는데 `push_received` 가 서버에 없다」를 아무도 설명하지 못한다.**
 *
 * 실기에서 정확히 그 조합을 봤고(2026-08-12, Android), `docs/18` §1 의 판정표가 그것을
 * 「TTL 초과로 버려졌다」로 오진한다 — 실제로는 알림이 왔다.
 *
 * ── 왜 Cache Storage 인가 ──────────────────────────────────
 *
 * SW 는 `localStorage` 를 볼 수 없고, `postMessage` 는 **페이지가 살아 있을 때만** 닿는다 —
 * push 는 대개 앱이 닫혀 있을 때 온다. 남는 것은 IndexedDB 와 Cache 둘이고, 한 건을
 * 덮어쓰는 데는 Cache 의 표면이 훨씬 작다.
 *
 * **이름 둘이 SW 와의 계약이다** (`sw-contract.test.ts` 가 대조한다). 어긋나면 진단이
 * 조용히 비고, 그러면 이 파일이 존재하는 이유가 사라진다.
 */

/** `public/sw.js` 의 `DIAG_CACHE`. */
export const SW_DIAG_CACHE = 'tv2m-diag';
/** `public/sw.js` 의 `DIAG_URL`. */
export const SW_DIAG_URL = '/__tv2m/last-report';

export interface SwReportDiag {
  /** `push_received` · `push_displayed` */
  type: string;
  /**
   * - `sent` — 서버가 202 로 받았다
   * - `rejected` — 요청은 갔는데 서버가 받지 않았다 (401 키 · 404 주소 · 400 모양)
   * - `failed` — 연결 자체가 안 됐다 (CORS · 망 · 서버 부재)
   * - `skipped` — 등록 URL 에 설정이 없어 **보내지도 않았다**
   */
  outcome: 'sent' | 'rejected' | 'failed' | 'skipped';
  detail: string;
  at: string;
}

/** 모양이 틀리면 `null`. **SW 가 쓴 값도 믿지 않는다** — 옛 판이 남아 있을 수 있다. */
export function parseSwReportDiag(raw: unknown): SwReportDiag | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const c = raw as Record<string, unknown>;
  const outcomes = ['sent', 'rejected', 'failed', 'skipped'];
  if (typeof c.type !== 'string' || c.type === '') return null;
  if (typeof c.outcome !== 'string' || !outcomes.includes(c.outcome)) return null;
  if (typeof c.detail !== 'string') return null;
  if (typeof c.at !== 'string' || c.at === '') return null;
  return {
    type: c.type,
    outcome: c.outcome as SwReportDiag['outcome'],
    detail: c.detail,
    at: c.at,
  };
}

/**
 * 사람이 읽을 한 줄.
 *
 * **`sent` 인데 서버에 기록이 없으면 그것은 서버 쪽 이야기다** — 그 구분이 이 값의
 * 존재 이유다.
 */
export function describeSwReportDiag(diag: SwReportDiag | null): string {
  if (diag === null) return '(없음 — 알림을 받은 적이 없다)';
  const when = diag.at.slice(11, 19);
  switch (diag.outcome) {
    case 'sent':
      return `${when} ${diag.type} 보고됨 (${diag.detail})`;
    case 'rejected':
      return `${when} ${diag.type} 서버가 거절 — ${diag.detail}`;
    case 'failed':
      return `${when} ${diag.type} 연결 실패 — ${diag.detail}`;
    default:
      return `${when} ${diag.type} 보내지 않음 — ${diag.detail}`;
  }
}

/** DOM 을 읽는 유일한 줄. Cache 를 못 쓰는 환경이면 `null` 이다. */
export async function readSwReportDiag(): Promise<SwReportDiag | null> {
  if (typeof caches === 'undefined') return null;
  try {
    const cache = await caches.open(SW_DIAG_CACHE);
    const response = await cache.match(SW_DIAG_URL);
    if (response === undefined) return null;
    return parseSwReportDiag(await response.json());
  } catch {
    return null;
  }
}
